import React, {useEffect, useState, useRef, useImperativeHandle, forwardRef} from 'react';
import {Button, Modal, Toast} from 'antd-mobile';
import {LocationOutline, PhoneFill} from 'antd-mobile-icons';
import {CheckCircleFill} from 'antd-mobile-icons';
import {copyToClipboard} from 'utils/copyToClipboard';
import {t} from "../../../i18n";
import {openInAppNavigation} from 'utils/appRuntime';
import {NativeCallbackManager, nativeCallbacks} from 'utils/NativeCallbackManager';
import {getUserInfo} from "../../../utils/auth";
import {isAppVersionGreaterThanOrEqual, kAndroidGoogleMapVersion, kAndroidWazeVersion, kIOSWazeVersion} from "../../../utils/versionUtils";

interface PickupPhoneAndNavigateProps {
    phone: string;
    address: string;
    tracking?: string;
    latitude?: number;
    longitude?: number;
    onDeliverySuccessFromNative?: () => void;
    onDeliveryFailedFromNative?: () => void;
    onCloseLongPressModal?: () => void;
}

export interface PickupPhoneAndNavigateRef {
    closeLongPressModal: () => void;
}

const PickupPhoneAndNavigate = forwardRef<PickupPhoneAndNavigateRef, PickupPhoneAndNavigateProps>(({ 
    phone, 
    address,
    tracking, 
    onDeliverySuccessFromNative, 
    onDeliveryFailedFromNative,
    latitude,
    longitude
}, ref) => {
    const [mapSelectionVisible, setMapSelectionVisible] = useState(false);
    const [longPressMapSelectionVisible, setLongPressMapSelectionVisible] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [showWaze, setShowWaze] = useState(false);
    const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
    const userInfo = getUserInfo();
    const reviewAccount = userInfo?.account?.id === '216';

    // 暴露关闭长按弹窗的方法给父组件
    useImperativeHandle(ref, () => ({
        closeLongPressModal: () => {
            setLongPressMapSelectionVisible(false);
        }
    }));

    useEffect(() => {
        //当前账号非提审账号时将且导航应用为inapp将其设置为google地图
        if (localStorage.getItem('navigationApp') === 'inapp' && !reviewAccount) {
            localStorage.setItem('navigationApp', 'google');
        }
    }, [reviewAccount]);

    useEffect(() => {
        // 检查是否显示 Waze 选项
        // Android: 版本 >= 8.1.0 才显示
        // iOS: 版本 >= 8.6.0 才显示
        const checkWazeVisibility = async () => {
            const targetVersion = isIOS ? kIOSWazeVersion : kAndroidWazeVersion;
            const shouldShow = await isAppVersionGreaterThanOrEqual(targetVersion);
            setShowWaze(shouldShow);
        };
        checkWazeVisibility();
    }, [isIOS]);

    useEffect(() => {
        // 检测是否为iOS设备
        setIsIOS(/iPad|iPhone|iPod/.test(navigator.userAgent));
        
        // 注册原生回调
        nativeCallbacks.on(NativeCallbackManager.DELIVERY_SUCCESS, handleDeliverySuccessFromNative);
        nativeCallbacks.on(NativeCallbackManager.DELIVERY_FAILED, handleDeliveryFailedFromNative);
        
        // 清理函数
        return () => {
            nativeCallbacks.off(NativeCallbackManager.DELIVERY_SUCCESS, handleDeliverySuccessFromNative);
            nativeCallbacks.off(NativeCallbackManager.DELIVERY_FAILED, handleDeliveryFailedFromNative);
        };
    }, []);

    // 处理来自原生的派送成功回调
    const handleDeliverySuccessFromNative = () => {
        onDeliverySuccessFromNative?.();
    };

    // 处理来自原生的派送失败回调
    const handleDeliveryFailedFromNative = () => {
        onDeliveryFailedFromNative?.();
    };

    const handleCall = () => {
        if (!phone || phone.trim() === '') {
            Toast.show({
                content: 'No phone number available',
                position: 'bottom',
            });
            return;
        }

        // 清理电话号码，只保留数字和+号
        const cleanPhone = phone.replace(/[^\d+]/g, '');

        if (!cleanPhone) {
            Toast.show({
                content: 'Invalid phone number format',
                position: 'bottom',
            });
            return;
        }

        try {
            // 尝试使用原生桥接
            window.location.href = `tel:${cleanPhone}`;
        } catch (error) {
            console.error('Phone call failed:', error);
            Toast.show({
                content: 'Phone call failed, please dial manually: ' + cleanPhone,
                position: 'bottom',
                duration: 3000
            });
        }
    };

    const handleNavigate = () => {
        if (!address || address.trim() === '') {
            Toast.show({
                content: 'No address available',
                position: 'bottom',
            });
            return;
        }

        // 获取用户设置的默认导航应用
        const defaultNavigationApp = localStorage.getItem('navigationApp') || 'google';
        
        // 如果用户设置了默认导航应用，直接使用
        if (defaultNavigationApp === 'apple' || defaultNavigationApp === 'google' || defaultNavigationApp === 'waze' || defaultNavigationApp === 'inapp') {
            const mapTypeMap: Record<string, 'Google' | 'Apple' | 'Waze' | 'Inapp'> = {
                'apple': 'Apple',
                'google': 'Google',
                'waze': 'Waze',
                'inapp': 'Inapp'
            };
            const mapType = mapTypeMap[defaultNavigationApp];
            handleSelectMap(mapType);
        } else {
            // 如果没有设置默认应用，显示选择对话框
            setMapSelectionVisible(true);
        }
    };

    const openUrl = (url: string) => {
        // 直接跳转；原生 WebView 要把自定义 scheme 拦截出去
        window.location.href = url;
    };

    // 具体方法：处理长按导航按钮
    const handleNavigateLongPress = () => {
        if (!address || address.trim() === '') {
            Toast.show({
                content: 'No address available',
                position: 'bottom',
            });
            return;
        }
        // iOS 长按则正常弹框，其他情况需要 showWaze 为真时才展示弹窗
        if (isIOS || showWaze) {
            setLongPressMapSelectionVisible(true);
        }
    };

    // 处理长按开始
    const handleNavigateTouchStart = () => {
        // iOS 长按则正常弹框，其他情况需要 showWaze 为真时才启动长按定时器
        if (isIOS || showWaze) {
            longPressTimerRef.current = setTimeout(() => {
                handleNavigateLongPress();
            }, 500); // 500ms 长按时间
        }
    };

    // 处理长按结束
    const handleNavigateTouchEnd = () => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
    };

    // 处理鼠标长按（桌面端支持）
    const handleNavigateMouseDown = () => {
        // iOS 长按则正常弹框，其他情况需要 showWaze 为真时才启动长按定时器
        if (isIOS || showWaze) {
            longPressTimerRef.current = setTimeout(() => {
                handleNavigateLongPress();
            }, 500);
        }
    };

    const handleNavigateMouseUp = () => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
    };

    const handleNavigateMouseLeave = () => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
    };

    // 获取当前默认导航应用
    const getDefaultNavigationApp = (): string => {
        return localStorage.getItem('navigationApp') || 'google';
    };

    // 处理仅本次导航的地图选择
    const handleSelectMapForThisTime = (mapType: 'Google' | 'Apple' | 'Waze') => {
        setLongPressMapSelectionVisible(false);
        handleSelectMap(mapType, false); // false 表示不保存为默认
    };

    const handleSelectMap = async (mapType: 'Google' | 'Apple' | 'Waze' | 'Copy' | 'Inapp', saveAsDefault: boolean = false) => {
        setMapSelectionVisible(false);

        if (mapType === 'Copy') {
            copyToClipboard(address)
                .then((success) => {
                    if (success) {
                        Toast.show({ content: 'Address copied', position: 'bottom' });
                    } else {
                        Toast.show({ content: 'Copy failed, please select address manually', position: 'bottom' });
                    }
                });
            return;
        }

        if (!address.trim()) {
            Toast.show({ content: 'No address', position: 'bottom' });
            return;
        }

        const encoded = encodeURIComponent(address);

        // —— 首先尝试 JS-Bridge（最稳） ——
        if (mapType === 'Google') {
            if (window.AndroidApp?.openNavigation) {
                // 判断版本号，决定使用哪种导航方式
                const androidGoogleOptimize = await isAppVersionGreaterThanOrEqual(kAndroidGoogleMapVersion);
                if (androidGoogleOptimize) {
                    // 版本 >= 8.0.1，使用优化后的 URL scheme 方式
                    window.AndroidApp.openNavigation(`google.navigation:q=${encoded}`);
                } else {
                    // 版本 < 8.0.1，使用地址字符串方式
                    window.AndroidApp.openNavigation(address);
                }
                return;
            }
            // —— 无桥接时：尝试调起 Google Maps ——

            if (isIOS) {
                const iOSScheme = `comgooglemaps://?daddr=${encoded}&directionsmode=driving`;
                openUrl(iOSScheme);
            } else {
                const androidScheme = `google.navigation:q=${encoded}`;
                openUrl(androidScheme);
                // Android WebView 若未被原生拦截，也 fallback
                setTimeout(() => openUrl(`https://www.google.com/maps/dir/?api=1&destination=${encoded}`), 2000);
            }
        }

        if (mapType === 'Apple') {
            if (!isIOS) {
                Toast.show({ content: 'Apple Maps only on iOS', position: 'bottom' });
                return;
            }
            openUrl(`maps://?q=${encoded}`); // iOS 官方文档
        }

        if (mapType === 'Waze') {
            // 构建 Waze URL
            let wazeAppUrl;
            if (latitude && longitude) {
                wazeAppUrl = `waze://?ll=${latitude},${longitude}&navigate=yes`; // 优先使用坐标导航（更准确）
            } else {
                wazeAppUrl = `waze://?q=${encoded}&navigate=yes`; // 没有经纬度时，使用地址导航
            }

            // Android 使用 JS-Bridge，iOS 使用 openUrl，其他设备使用网页版
            if (window.AndroidApp?.openNavigation) {
                // Android：通过原生方法打开，原生端会检测应用是否安装并处理 fallback
                window.AndroidApp.openNavigation(wazeAppUrl);
            } else {
                //IOS通过链接自动打开waze app   其他：使用网页版 Waze
                const wazeWebUrl = latitude && longitude
                    ? `https://www.waze.com/ul?ll=${latitude},${longitude}&navigate=yes`
                    : `https://www.waze.com/ul?q=${encoded}&navigate=yes`;
                openUrl(wazeWebUrl);
            }
        }

        if (mapType === 'Inapp') {
            // latitude：34.17501, longitude:-118.39651
            if (latitude && longitude) {
                openInAppNavigation(tracking || '', phone, address, latitude.toString(), longitude.toString());
            } else {
                Toast.show({content: 'latitude ｜ longitude is empty',position: 'bottom',});
            }
        }
    };

    return (
        <>
            <div className='flex gap-2'>
                <div className='flex-1'>
                    <Button
                        color='default'
                        shape='rounded'
                        onClick={handleCall}
                        block
                        style={{ '--background-color': '#f0f0f0' }}
                    >
                        <div className='flex flex-row justify-center items-center gap-2'>
                            <PhoneFill />
                            <div>{t('pages.delivery.orderDetail.call')}</div>
                        </div>
                    </Button>
                </div>
                <div
                    className='flex-1'
                    onTouchStart={handleNavigateTouchStart}
                    onTouchEnd={handleNavigateTouchEnd}
                    onMouseDown={handleNavigateMouseDown}
                    onMouseUp={handleNavigateMouseUp}
                    onMouseLeave={handleNavigateMouseLeave}
                >
                    <Button
                        color='default'
                        shape='rounded'
                        onClick={handleNavigate}
                        block
                        style={{ '--background-color': '#f0f0f0' }}
                    >
                        <div className='flex flex-row justify-center items-center gap-2'>
                            <LocationOutline />
                            <div>{t('pages.delivery.orderDetail.navigate')}</div>
                        </div>
                    </Button>
                </div>
            </div>

            {/* Map selection modal (default) */}
            <Modal
                visible={mapSelectionVisible}
                title= {t('pages.delivery.orderDetail.selectMapTitle')}
                content={(
                    <div className="py-4">
                        <div className="py-3 px-4 text-base text-center border-b border-gray-100"
                             onClick={() => handleSelectMap('Google')}>
                            {t('pages.delivery.orderDetail.googleMaps')}
                        </div>
                        {isIOS && (
                            <div className="py-3 px-4 text-base text-center border-b border-gray-100"
                                 onClick={() => handleSelectMap('Apple')}>
                                {t('pages.delivery.orderDetail.appleMaps')}
                            </div>
                        )}
                        {showWaze && (
                            <div className="py-3 px-4 text-base text-center border-b border-gray-100"
                                 onClick={() => handleSelectMap('Waze')}>
                                {t('pages.delivery.orderDetail.wazeMaps')}
                            </div>
                        )}
                        <div className="py-3 px-4 text-base text-center"
                             onClick={() => handleSelectMap('Copy')}>
                            {t('pages.delivery.orderDetail.copyAddress')}
                        </div>
                    </div>
                )}
                closeOnAction
                actions={[
                    {
                        key: 'cancel',
                        text: t('pages.delivery.orderDetail.mapCancel'),
                        onClick: () => setMapSelectionVisible(false),
                    }
                ]}
            />

            {/* Long press map selection modal (for this time only) */}
            <Modal
                visible={longPressMapSelectionVisible}
                title={t('pages.delivery.orderDetail.selectMapForThisTimeTitle')}
                closeOnMaskClick
                onClose={() => setLongPressMapSelectionVisible(false)}
                content={(
                    <div className="py-4">
                        <div
                            className="py-3 px-4 text-base text-center border-b border-gray-100 flex items-center justify-between"
                            onClick={() => handleSelectMapForThisTime('Google')}
                        >
                            <span>{t('pages.delivery.orderDetail.googleMaps')}</span>
                            {getDefaultNavigationApp() === 'google' ? (
                                <CheckCircleFill color="#00b578" style={{fontSize: '20px'}}/>
                            ) : (
                                <div style={{
                                    width: '20px',
                                    height: '20px',
                                    borderRadius: '50%',
                                    border: '2px solid #c9c9c9',
                                    flexShrink: 0
                                }}/>
                            )}
                        </div>
                        {isIOS && (
                            <div
                                className="py-3 px-4 text-base text-center border-b border-gray-100 flex items-center justify-between"
                                onClick={() => handleSelectMapForThisTime('Apple')}
                            >
                                <span>{t('pages.delivery.orderDetail.appleMaps')}</span>
                                {getDefaultNavigationApp() === 'apple' ? (
                                    <CheckCircleFill color="#00b578" style={{fontSize: '20px'}}/>
                                ) : (
                                    <div style={{
                                        width: '20px',
                                        height: '20px',
                                        borderRadius: '50%',
                                        border: '2px solid #c9c9c9',
                                        flexShrink: 0
                                    }}/>
                                )}
                            </div>
                        )}
                        {showWaze && (
                            <div
                                className="py-3 px-4 text-base text-center flex items-center justify-between"
                                onClick={() => handleSelectMapForThisTime('Waze')}
                            >
                                <span>{t('pages.delivery.orderDetail.wazeMaps')}</span>
                                {getDefaultNavigationApp() === 'waze' ? (
                                    <CheckCircleFill color="#00b578" style={{fontSize: '20px'}}/>
                                ) : (
                                    <div style={{
                                        width: '20px',
                                        height: '20px',
                                        borderRadius: '50%',
                                        border: '2px solid #c9c9c9',
                                        flexShrink: 0
                                    }}/>
                                )}
                            </div>
                        )}
                    </div>
                )}
                closeOnAction
                actions={[
                    {
                        key: 'cancel',
                        text: t('pages.delivery.orderDetail.mapCancel'),
                        onClick: () => setLongPressMapSelectionVisible(false),
                    }
                ]}
            />
        </>
    );
});

PickupPhoneAndNavigate.displayName = 'PickupPhoneAndNavigate';

export default PickupPhoneAndNavigate;

